/**
 * Renders the assistant-ui styled <Thread/> over the EXISTING `useChat` instance
 * owned by SphereAIChatSection — chat transport, MCP `onToolCall`, deferred-tool
 * watcher, server-side persistence — with NO change to the chat engine.
 *
 * We do NOT create a new chat here: the live `useChat(...)` instance is passed in
 * and adapted via `useAISDKRuntime`, so the styled thread drives the same engine +
 * the same `addToolOutput` loop.
 */
import type { useChat } from "@ai-sdk/react";
import { AssistantRuntimeProvider, useComposerRuntime } from "@assistant-ui/react";
import { useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import { isToday, isYesterday } from "date-fns";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	InlineRefResolverProvider,
	type MessageAction,
	Thread,
	type ThreadComponents,
} from "@mdotm/shadcn/assistant-ui";
import { CircularProgressBar, Text, textDataAttr } from "@mdotm/shadcn/legacy/components";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { AppContextChip } from "./AppContextChip";
import { isCurrentPage, pageKey, resolveToolLink } from "./toolLinks";
import type { SphereAIChatGate } from "./chatAvailability";
import { withSendTimestamp } from "./messageTimestamps";
import { uploadAttachmentAdapter } from "./attachments/uploadAttachmentAdapter";
import { exportMessageAsDocx } from "./export/exportMessageAsDocx";
import { sphereAIChartTools } from "./charts/SphereAIChart";
import { SphereAIComposerChoice, sphereAIChoiceTools } from "./choices/SphereAIChoice";
import { SphereAIComposerSettings } from "./SphereAIComposerSettings";
import { SphereAIConsultedPages } from "./SphereAIConsultedPages";
import { SphereAIDisclaimer } from "./SphereAIDisclaimer";
import { SphereAIPanelPortalProvider } from "./SphereAIPanelPortal";
import { SphereAIWelcome } from "./SphereAIWelcome";
import { SphereAILoopNotice } from "./SphereAILoopNotice";
import { SphereAIToolFallback } from "./SphereAIToolFallback";
import { LocalWebSpeechDictationAdapter } from "./voice/localWebSpeechDictationAdapter";
import { retryAfterSeconds } from "./rateLimit";
import { sphereAIWorkingLabels } from "./workingLabels";
import { zoneLabel } from "./zoneLabel";
import "./sphere-ai-thread.css";

/**
 * The Thread's single `Composer` slot, shared by the affordances that live at the bottom of the
 * thread: the loop guard's "keep going?" notice (above the input — see SphereAILoopNotice), the
 * pending `radio`/`select` picker (which replaces the input — see SphereAIComposerChoice) and the
 * AI disclaimer notice, always under the input (see SphereAIDisclaimer). The first two render
 * nothing when they have nothing to say, so with both idle this is the default composer plus the
 * disclaimer.
 */
function SphereAIComposerArea({ children }: React.PropsWithChildren): React.JSX.Element {
	return (
		<div className="flex w-full flex-col gap-2">
			<SphereAILoopNotice />
			<SphereAIComposerChoice>{children}</SphereAIComposerChoice>
			<SphereAIDisclaimer />
		</div>
	);
}

/**
 * The by-name generative-UI registry handed to the styled Thread: the `plot_chart` chart
 * renderer plus the `radio` / `select` interactive choice renderers. A stable module constant
 * so the Thread can memoize its part grouping across renders.
 */
const sphereAIThreadComponents: ThreadComponents = {
	Welcome: SphereAIWelcome,
	Tools: { ...sphereAIChartTools, ...sphereAIChoiceTools },
	// Generic tool rows live inside the message's execution panel, whose step list names every
	// tool that ran for everyone; the expandable arguments/result body behind that name is
	// privileged — root/impersonated only. See toolDetails.ts.
	ToolFallback: SphereAIToolFallback,
	// "Pages consulted" under the answer, derived from the turn's `web-fetch` calls rather
	// than from the model's prose — the one piece of attribution in the message that cannot
	// be misremembered. See SphereAIConsultedPages.
	AssistantMessageExtra: SphereAIConsultedPages,
	// Badge above the composer input advertising the current-screen context injected
	// into each turn, with a ✕ to opt out. Its state is provided by SphereAIChatSection.
	ComposerContext: AppContextChip,
	// Bottom-of-thread affordances: the loop guard's "keep going?" notice, and the choice picker
	// that takes the composer over while a `radio`/`select` question is pending. Both are fed
	// their state by SphereAIChatSection; with neither active this is the default composer.
	Composer: SphereAIComposerArea,
	// The settings gear, in the composer action row before the mic/send buttons. Its state is
	// persisted (see chatSettings) and read straight from the atom by the transport and the MCP
	// client, so it needs nothing from SphereAIChatSection.
	ComposerActionExtra: SphereAIComposerSettings,
};

/** The full return object of `useChat` (assistant-ui adapts the instance, not its pieces). */
type ChatHelpers = ReturnType<typeof useChat>;

/**
 * Composer attachments. Every file type is uploaded to the sphere-mcp server
 * (see uploadAttachmentAdapter, MDOT-24551): images are referenced by a signed
 * `image_url` the model sees directly; other files are referenced by `fileId`
 * and read on demand via the `file-read` / `file-render_pages` MCP tools — so
 * nothing binary is dumped into the prompt as tokens.
 */
const attachmentAdapter = uploadAttachmentAdapter;

/**
 * Voice input (the primary composer mic button). Registering a `dictation` adapter
 * is what flips on the `thread.capabilities.dictation` flag the styled Thread gates
 * the mic UI behind — there is no other wiring.
 *
 * This is the on-device Web Speech adapter (`processLocally`): streaming, native, and
 * private — no cloud round-trip. It's Chrome-only; where unsupported no adapter is
 * registered, the `dictation` capability stays off, and the mic button is hidden entirely.
 */
const dictationAdapter = LocalWebSpeechDictationAdapter.isSupported()
	? new LocalWebSpeechDictationAdapter({ language: navigator.language })
	: undefined;

const runtimeAdapters = {
	adapters: { attachments: attachmentAdapter, ...(dictationAdapter && { dictation: dictationAdapter }) },
} as const;

/** Default gate: nothing gets in the way. A module constant so the default prop has one identity. */
const READY_GATE: SphereAIChatGate = { status: "ready" };

export function SphereAIThread({
	chat,
	gate = READY_GATE,
	initialDraft,
	onDraftApplied,
}: {
	chat: ChatHelpers;
	/**
	 * Whether the chat can be shown at all, and why not when it can't (see chatAvailability.ts).
	 *
	 * While `"loading"` the Thread is withheld so a remount with momentarily empty messages doesn't
	 * flash the styled welcome screen (see restoredChatId in the parent). `"unavailable"` is the case
	 * the loader used to swallow: a precondition FAILED and will never resolve on its own — the stored
	 * transcript could not be read (sphere-mcp unreachable, CORS, 401), or the assistant's tools could
	 * not be discovered — and holding the loader forever tells the user "still working on it" about
	 * work that has already stopped.
	 */
	gate?: SphereAIChatGate;
	/** Text to prefill the composer with once (a question relaunched from History), not sent. */
	initialDraft?: string | null;
	/** Called after the draft has been applied, so the owner can clear it. */
	onDraftApplied?: () => void;
}): React.JSX.Element {
	// The composer's send path, wrapped so the user's message carries its send time from the frame it
	// first renders — see withSendTimestamp for what reads that time and why the owner's persist-time
	// backfill is too late for it. assistant-ui's runtime is what mints the user message (its `onNew`
	// calls `chat.sendMessage`) and it takes the chat helpers as a plain object, so overriding that
	// one method is the whole change: every other field, `messages` included, is passed through by
	// reference and the chat engine is untouched. A fresh object per render is what `useChat` itself
	// returns, so there is nothing to memoize.
	const stampedChat: ChatHelpers = {
		...chat,
		sendMessage: (message, options) => chat.sendMessage(message && withSendTimestamp(message, Date.now()), options),
	};

	// The load-bearing call: adapt the existing, externally-owned useChat instance.
	const runtime = useAISDKRuntime(stampedChat, runtimeAdapters);

	// Copy for the sticky day separators. Named days for the two the user thinks of by name, the
	// localized date for everything older — the same rule (and the same formatter) as the
	// notification center's date groups, so the two dated lists in the product agree.
	const { t } = useTranslation();
	const { formatDate, formatTime } = useLocaleFormatters();
	const daySeparatorLabel = useCallback(
		(timestampMs: number) => {
			const date = new Date(timestampMs);
			if (isToday(date)) {
				return t("sphereAi.chat.today");
			}
			if (isYesterday(date)) {
				return t("sphereAi.chat.yesterday");
			}
			return formatDate(date);
		},
		[t, formatDate],
	);

	// Copy for the per-message time. It names its zone for the same reason the resumed bar's does
	// (see zoneLabel): the hour is the VIEWER's, and a transcript reopened from History can easily
	// have been written on another clock — an hour with nothing to anchor it invites the reader to
	// assume it's theirs. The zone is stated everywhere we state an hour, so the two never disagree.
	const messageTimeLabel = useCallback(
		(timestampMs: number) => `${formatTime(timestampMs)} ${zoneLabel(new Date(timestampMs))}`,
		[formatTime],
	);

	// A link to the page the user is on is shown as its label; a new page or view mints a new resolver.
	const { pathname, search } = useLocation();
	const currentPage = pageKey(pathname, search);
	const resolveOffPage = useCallback(
		(ref: string) => {
			const url = resolveToolLink(ref);
			return url !== undefined && isCurrentPage(url, currentPage) ? undefined : url;
		},
		[currentPage],
	);

	// "Export as Word" in the message menu, beside the generic thread's own Export as Markdown.
	// It lives here rather than in the library because it is the only export that can carry the
	// message's CHARTS: a `plot_chart` part is generative UI whose spec only this app understands,
	// and the markdown export drops it (see export/exportMessageAsDocx). English like the built-in
	// item next to it — the Sphere AI surface isn't translated, and a half-translated menu is worse
	// than an untranslated one.
	//
	// Fed `answerParts`, so the "let me check…" narration the thread folds into the execution panel
	// stays out of the document, exactly as it stays out of the prose.
	const messageActions = useMemo<readonly MessageAction[]>(
		() => [
			{
				label: "Export as Word",
				icon: "file",
				run: ({ answerParts, createdAt }) => {
					const written = DateTime.fromMillis(createdAt ?? Date.now());
					return exportMessageAsDocx(answerParts, {
						// Ordered, punctuation-free and second-precise: the file name has to sort by time in
						// a download folder and survive every filesystem, which rules out the localized date.
						fileName: `sphere-ai-${written.toFormat("yyyyLLdd-HHmmss")}.docx`,
						title: "Sphere AI",
						subtitle: createdAt === undefined ? undefined : `${formatDate(createdAt)}, ${messageTimeLabel(createdAt)}`,
					});
				},
			},
		],
		[formatDate, messageTimeLabel],
	);

	// The panel element, published so floating surfaces inside the panel can portal INTO it rather
	// than to `document.body` — everything that makes this look like the platform is declared on
	// `.sphere-ai-thread` and only reaches its subtree. See SphereAIPanelPortal.
	const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);

	return (
		<AssistantRuntimeProvider runtime={runtime}>
			{/* Prefill the composer when a question is relaunched from History (must live under the
			    runtime provider to reach the composer). */}
			<ComposerDraftSetter draft={initialDraft} onApplied={onDraftApplied} />
			<div
				ref={setPanelElement}
				className="sphere-ai-thread bg-background relative h-full w-full"
				{...textDataAttr("Body/L/Book")}
			>
				{/* `components.Tools` registers the `plot_chart`, `radio` and `select` renderers.
				    The composer placeholder carries the empty-state invitation (the welcome no longer
				    repeats it as a text line). */}
				{gate.status === "ready" ? (
					<SphereAIPanelPortalProvider value={panelElement}>
						{/* Links the model wrote at a withheld address — [here](sphere-handle://doc-…)
						    — get the real target from the links the client is holding, in place,
						    inside the model's own sentence. This provider is the ONLY thing that
						    shows a tool link: there is no card and no appended suffix; see
						    toolLinks.ts. */}
						<InlineRefResolverProvider resolve={resolveOffPage}>
							<Thread
								components={sphereAIThreadComponents}
								composerPlaceholder="How can I help you?"
								// Per-message time back on, but it fades in only under the pointer — so the resting
								// transcript stays as quiet as it was without it, while the day separators keep
								// carrying the coarse "when" you read at a glance.
								messageTimeLabel={messageTimeLabel}
								daySeparatorLabel={daySeparatorLabel}
								messageActions={messageActions}
								// Flag-guarded: unset in every environment that has not turned
								// `featureFlags.sphereAI.playfulWorkingLabels` on, which leaves the panel
								// on its own plain "Working".
								workingLabels={sphereAIWorkingLabels()}
							/>
						</InlineRefResolverProvider>
					</SphereAIPanelPortalProvider>
				) : gate.status === "unavailable" ? (
					/* A precondition failed and will not resolve on its own. An indeterminate loader here is
					   a LIE about work still being in progress, and it never clears — see
					   SphereAIThreadUnavailable. */
					<SphereAIThreadUnavailable cause={gate.cause} onRetry={gate.onRetry} retryAfterMs={gate.retryAfterMs} />
				) : (
					/* Restoring a stored conversation. Was a blank canvas, which read as "the chat is
					   gone" now that reopening a transcript from History is a normal move; the loader
					   fades in only if the wait is long enough to notice (see sphere-ai-thread.css). */
					<div aria-busy="true" className="flex h-full w-full items-center justify-center">
						<CircularProgressBar className="sphere-ai-delayed-loader w-6" value="indeterminate" />
					</div>
				)}
			</div>
		</AssistantRuntimeProvider>
	);
}

/**
 * Shown in place of the Thread when a precondition of the chat failed: this conversation's stored
 * transcript could not be read, or the assistant's tools could not be discovered.
 *
 * It exists because the alternative isn't a blank canvas, it's a loader that spins forever: the
 * restore is applied imperatively (setMessages on the live useChat instance), so a failed read has
 * no success callback to release the gate and nothing downstream ever re-renders. Retry re-runs the
 * read that failed, which is the whole recovery when the cause is a dropped connection or an
 * expired session.
 *
 * The copy names what actually didn't load, because the two causes call for different moves. A
 * failed transcript read is about THIS conversation — the assistant is fine, and opening another
 * one works. Failed tool discovery is about the SERVICE: no tools means the model can do nothing in
 * the product, so the honest screen says Sphere AI is unavailable rather than handing the user a
 * composer and a bot that will deny its own capabilities.
 *
 * Mirrors the History empty state's identity zone (title → subtitle) so the panel's two "nothing to
 * show" screens read as the same surface; centered in the thread area rather than top-aligned,
 * because it replaces a transcript, not a list.
 */
function SphereAIThreadUnavailable({
	cause,
	onRetry,
	retryAfterMs,
}: {
	cause: "assistant" | "conversation";
	onRetry?: () => Promise<void>;
	retryAfterMs?: number;
}): React.JSX.Element {
	const { t } = useTranslation();
	// A rate limit is a different fact from an outage and deserves different words: the server is
	// answering fine, it has simply had enough for now, and — unlike an outage — it told us when to
	// come back. It also outranks the cause, for both causes: what to do about a 429 is wait, whatever
	// it was that got refused. `retryAfterSeconds` is null when the server didn't say, in which case
	// the cause's own copy is still the honest one.
	const waitSeconds = retryAfterSeconds(retryAfterMs);
	return (
		<div
			role="alert"
			data-qualifier="SphereAI/unavailable"
			data-cause={cause}
			className="flex h-full w-full items-center justify-center px-4"
		>
			<div className="flex max-w-xs flex-col items-center gap-2 text-center">
				<Text type="Body/L/Medium" color={themeCSSVars.palette_N700}>
					{waitSeconds !== null
						? t("sphereAi.chat.restoreErrorRateLimitedTitle")
						: cause === "assistant"
							? t("sphereAi.chat.unavailableTitle")
							: t("sphereAi.chat.restoreErrorTitle")}
				</Text>
				<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N400}>
					{waitSeconds !== null
						? t("sphereAi.chat.restoreErrorRateLimited", { count: waitSeconds })
						: cause === "assistant"
							? t("sphereAi.chat.unavailableSubtitle")
							: t("sphereAi.chat.restoreErrorSubtitle")}
				</Text>
				{onRetry && (
					<Button onClickAsync={onRetry} style={{ minWidth: 0, borderRadius: 9999 }} palette="secondary" size="small">
						{t("RETRY")}
					</Button>
				)}
			</div>
		</div>
	);
}

/**
 * Applies a one-shot draft to the thread composer: when `draft` becomes a non-empty string (a
 * question relaunched from History), it sets the composer text without sending, focuses the input,
 * and calls `onApplied` so the owner clears the draft. A ref guards against re-applying the same
 * draft across re-renders / React StrictMode. Must render under the AssistantRuntimeProvider.
 */
function ComposerDraftSetter({ draft, onApplied }: { draft?: string | null; onApplied?: () => void }): null {
	const composer = useComposerRuntime();
	const appliedRef = useRef<string | null>(null);
	useEffect(() => {
		if (!draft || appliedRef.current === draft) {
			return;
		}
		appliedRef.current = draft;
		composer.setText(draft);
		// The runtime has no focus method; focus the rendered input (past this render) so the
		// caret lands ready to edit/send, mirroring the welcome suggestions.
		requestAnimationFrame(() => {
			const input = document.querySelector<HTMLTextAreaElement>(".aui-thread-root .aui-composer-input");
			if (input) {
				input.focus({ preventScroll: true });
				input.setSelectionRange(input.value.length, input.value.length);
			}
		});
		onApplied?.();
	}, [draft, composer, onApplied]);
	return null;
}
